'use client';

import { Handset } from '@sgcmpteltd/uikit-react';
import {
  MENU_BORDER_RADIUS_EXTRA_LARGE,
  MENU_LAYER_POSITION_FIXED,
  Select,
} from '@sgcmpteltd/uikit-react/components/select';
import cx from 'clsx';
import Link from 'next/link';
import { useState } from 'react';

import {
  ListTypeLabel,
} from '@/app/(routes)/listing/_components/page-content/search-list/car-card/listing-type-label'; // @suppress longLineCheck
import { ElitesLabel } from '@/components/elites-label';
import { Tooltip } from '@/components/tooltip';
import { joinSgcmSite } from '@/utils/url/join/public';

import styles from './styles.module.scss';
import type { Component } from './types';

export const ParallelDealers: Component = ({
  carDetailData,
  carPromo,
  onOpenReadyStocks,
}) => {
  const [subModelsFilter, setSubModelsFilter] = useState('');
  const [pricingFilter, setPricingFilter] = useState('');
  const [showOtherPricingSchemes, setShowOtherPricingSchemes] = useState(false);
  const [filterSubModelId, setFilterSubModelId] = useState<number | string>('');

  const adverts = carDetailData?.parallelDealers?.map(
    dealer => dealer.adverts,
  ).flat();

  const availableSubModels = carDetailData!.subModels
    .filter(subModel => adverts?.some(
      advert => advert.subModelId === subModel.id,
    ))
    .map((subModel) => {
      const matchingAdverts = adverts?.filter(
        advert => advert.subModelId === subModel.id,
      );
      return { ...subModel, adverts: matchingAdverts };
    });

  const handleReadyStocksOnClick = (id: number) => {
    onOpenReadyStocks?.(id);
  };

  const handleSubModelsFilterOnClick = (data: string | number) => {
    setSubModelsFilter(data as string);

    if (data === '') {
      setFilterSubModelId('');
    } else {
      setFilterSubModelId(data);
    }
  };

  const handlePricingFilterOnClick = (data: string | number | undefined) => {
    setPricingFilter(data as string);

    if (data === 'other') {
      setShowOtherPricingSchemes(true);
    } else {
      setShowOtherPricingSchemes(false);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) {
      return '';
    }
    const date = new Date(dateStr);
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleString('en', { month: 'short' });
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  };

  const pricingSchemas = carDetailData!.parallelDealers?.map(
    dealer => dealer.pricingSchema,
  );

  const uniquePricingSchemas = [
    ...new Set(
      pricingSchemas?.filter(schema => schema && schema !== ''),
    ),
  ];

  const noCarPromo = carPromo.length === 0;

  return (
    <div className={styles.containerParallelDealers}>
      <div className={noCarPromo
        ? styles.containerFullFilter
        : styles.containerFilter}>
        <div className={noCarPromo
          ? styles.containerFullDealerFilter
          : styles.containerDealerFilter}>
          <div className={styles.textTitle}>Dealer</div>
        </div>
        <div className={noCarPromo
          ? styles.containerFullSubModelFilter
          : styles.containerSubModelFilter}>
          <div className={styles.textTitle}>Submodel</div>
          <div className={styles.containerSubModelsFilter}>
            <Select
              headerClassName={cx(
                styles.containerSelectHeader,
                styles.isHeader,
                styles.isHeaderHeight,
              )}
              menuAlignment="right"
              menuBorderRadius={MENU_BORDER_RADIUS_EXTRA_LARGE}
              menuContainerClassName={cx(
                styles.containerSelectMenu,
                styles.isWidth,
              )}
              menuLayerPosition={MENU_LAYER_POSITION_FIXED}
              optionList={
                availableSubModels.map(submodel => ({
                  value: submodel.id,
                  text: submodel.name,
                }))
              }
              placeholder={{
                text: 'All Submodel',
              }}
              value={subModelsFilter}
              onChange={handleSubModelsFilterOnClick} />
          </div>
        </div>
        <div className={noCarPromo
          ? styles.containerFullPricingFilter
          : styles.containerPricingFilter}>
          <div className={styles.textTitle}>Pricing</div>
          <div className={styles.containerSubModelsFilter}>
            <Select
              headerClassName={cx(
                styles.containerSelectHeader,
                styles.isHeader,
                styles.isHeaderHeight,
              )}
              menuAlignment="right"
              menuBorderRadius={MENU_BORDER_RADIUS_EXTRA_LARGE}
              menuContainerClassName={cx(
                styles.containerSelectMenu,
                styles.isWidth,
              )}
              menuLayerPosition={MENU_LAYER_POSITION_FIXED}
              optionList={
                uniquePricingSchemas.map(pricingSchema => ({
                  value: pricingSchema || '',
                  text: pricingSchema || '',
                })).concat({
                  value: 'other',
                  text: 'Other pricing schemes',
                })
              }
              placeholder={{
                text: 'Filter',
                isOptionHidden: true,
              }}
              value={pricingFilter}
              onChange={handlePricingFilterOnClick} />
          </div>
        </div>
      </div>
      {carDetailData?.parallelDealers
        ?.filter(dealer => dealer.adverts
          .some(advert => !filterSubModelId
          || advert.subModelId === filterSubModelId))
        .map(dealer => (
          <div
            key={dealer.id}
            className={noCarPromo
              ? styles.containerFullContent
              : styles.containerContent}>
            <div className={noCarPromo
              ? styles.containerFullDealerContent
              : styles.containerDealerContent}>
              <div className={styles.containerDealerText}>
                <Link
                  className={styles.textDealerName}
                  href={joinSgcmSite(`/new_cars/newcars_PIdealer.php?dcode=${dealer.id}`)}>
                  {dealer.name}
                </Link>
                {dealer.isElites && (
                  <div className={styles.elitesContainer}>
                    <ElitesLabel />
                  </div>
                )}
                {dealer.adverts.some(advert => advert.stocks.length > 0) && (
                  <button
                    className={styles.btnReadyStocks}
                    onClick={() => handleReadyStocksOnClick(dealer.id)}>
                    Ready Stocks
                  </button>
                )}
                <div className={styles.containerNumber}>
                  <div className={styles.containerIcon}>
                    <Handset
                      aria-label="handset icon"
                      size={16} />
                  </div>
                  <div
                    className={styles.containerPhoneNumber}>
                    {(() => {
                      const numbers = dealer.whatsAppNumber
                        && !dealer.hotlines.includes(dealer.whatsAppNumber)
                        ? [dealer.whatsAppNumber, ...dealer.hotlines]
                        : dealer.hotlines;

                      return numbers.map((number, index) => (
                        <>
                          {index > 0
                          && <span className={styles.separator}> | </span>}
                          <span
                            className={styles.textNumber}>
                            {`${number.slice(0, 4)} ${number.slice(4)}`}
                          </span>
                        </>
                      ));
                    })()}
                  </div>
                </div>
                <div className={styles.textUpdated}>
                  Price Updated:
                  {' '}
                  {formatDate(dealer.updatedAt)}
                </div>
              </div>
            </div>
            <div>
              {dealer.adverts
                ?.filter(advert => !filterSubModelId
                || advert.subModelId === filterSubModelId)
                .map(advert => (
                  <div key={advert.id}>
                    <div className={styles.containerSubModelsContent}>
                      <div className={noCarPromo
                        ? styles.textFullSubModelName
                        : styles.textSubModelName}>
                        {availableSubModels
                          .filter(subModel => subModel.id === advert.subModelId)
                          .map(subModel => subModel.name)}
                        <div className={styles.containerTags}>
                          {availableSubModels
                            .filter(subModel => subModel.id
                            === advert.subModelId)
                            .some(subModel => subModel.isFacelift)
                            && (
                              <ListTypeLabel
                                labelSize={10}
                                type="facelift" />
                            )}
                          {availableSubModels
                            .filter(subModel => subModel.id
                            === advert.subModelId)
                            .some(subModel => subModel.isNewVariant)
                            && (
                              <ListTypeLabel
                                labelSize={10}
                                type="newSubmodel" />
                            )}
                          { advert.remark !== '' && (
                            <Tooltip
                              className={cx(
                                styles.containerTooltipPosition,
                                styles.advertRemarkTooltip,
                              )}
                              content={(
                                <div className={
                                  styles.containerAdvertRemarkTooltipContent
                                }>
                                  {advert.remark}
                                </div>
                              )}>
                              <svg
                                className={styles.iconTooltip}
                                fill="none"
                                height="14"
                                viewBox="0 0 15 14"
                                width="15"
                                xmlns="http://www.w3.org/2000/svg">
                                <path
                                  // eslint-disable-next-line @stylistic/max-len
                                  d="M0.5 7.4375C0.5 7.1277 0.5 6.81762 0.5 6.50781C0.50875 6.47336 0.522969 6.43918 0.52543 6.40418C0.563437 5.84746 0.66625 5.30141 0.860664 4.78023C1.8423 2.14867 3.72055 0.581875 6.48418 0.0792969C6.67531 0.0445703 6.86973 0.0259766 7.0625 0C7.3723 0 7.68238 0 7.99219 0C8.02227 0.00820313 8.05207 0.021875 8.0827 0.0240625C8.92133 0.0833984 9.72551 0.286289 10.4777 0.658164C12.6756 1.74426 13.9963 3.49617 14.4188 5.91609C14.453 6.11215 14.4732 6.31066 14.5 6.50781V7.4375C14.4915 7.4725 14.4803 7.50695 14.4746 7.54223C14.4111 7.92723 14.375 8.31934 14.2804 8.69641C13.9105 10.1727 13.1293 11.4002 11.9666 12.378C10.977 13.2103 9.83871 13.729 8.55629 13.9215C8.36844 13.9497 8.18031 13.974 7.99219 14H7.0625C7.0234 13.991 6.98484 13.9792 6.9452 13.9735C6.65781 13.9314 6.36605 13.9081 6.08305 13.8463C4.5291 13.5067 3.23301 12.7263 2.19695 11.5218C1.32551 10.5087 0.783281 9.33899 0.580117 8.01527C0.550586 7.82305 0.526523 7.63 0.5 7.4375ZM6.70648 6.1518C6.64113 6.1518 6.59191 6.1518 6.5427 6.1518C5.64035 6.1518 4.73828 6.14934 3.83594 6.15289C3.2825 6.15508 2.89559 6.58574 2.97898 7.0968C3.04789 7.5198 3.38012 7.7916 3.84059 7.79297C4.74293 7.79543 5.645 7.79379 6.54734 7.79379C6.59602 7.79379 6.64496 7.79379 6.70648 7.79379C6.70648 7.85641 6.70648 7.90535 6.70648 7.9543C6.70648 8.85664 6.70402 9.75871 6.7073 10.6611C6.70922 11.2328 7.15273 11.6159 7.6884 11.5164C8.08324 11.4431 8.34656 11.0996 8.34738 10.6493C8.34902 9.74695 8.34793 8.84488 8.34793 7.94254V7.79379H8.51855C9.39793 7.79379 10.2776 7.79598 11.157 7.79297C11.7295 7.79105 12.1137 7.35027 12.0164 6.81488C11.9442 6.41895 11.6013 6.15344 11.1523 6.15262C10.2683 6.15098 9.38426 6.15207 8.50023 6.15207C8.45211 6.15207 8.40398 6.15207 8.34793 6.15207C8.34793 6.08398 8.34793 6.03449 8.34793 5.98473C8.34793 5.1007 8.35039 4.21668 8.34684 3.33266C8.34492 2.83309 7.9818 2.45547 7.52133 2.4623C7.05977 2.46914 6.71223 2.83719 6.70703 3.33047C6.70348 3.65395 6.70648 3.97742 6.70621 4.30117C6.70621 4.91148 6.70648 5.52125 6.70648 6.1518Z"
                                  fill="#F0484E" />
                              </svg>
                            </Tooltip>
                          )}
                        </div>
                      </div>
                      {(() => {
                        if (!showOtherPricingSchemes
                          || dealer.pricingSchema2 === null) {
                          return (
                            <div className={styles.containerPricingContent}>
                              {advert.price === 0 || advert.price === null
                                ? (
                                  <div className={styles.textPrice}>POA</div>
                                )
                                : (
                                  <div className={styles.textPrice}>
                                    $
                                    {new Intl.NumberFormat()
                                      .format(advert.price)}
                                  </div>
                                )}
                              <div className={styles.textPriceDesc}>
                                {dealer.pricingSchema}
                              </div>
                            </div>
                          );
                        }
                        return (
                          <div className={styles.containerPricingContent}>
                            {advert.price2 === 0 || advert.price2 === null
                              ? (
                                <div className={styles.textPrice}>POA</div>
                              )
                              : (
                                <div className={styles.textPrice}>
                                  $
                                  {new Intl.NumberFormat()
                                    .format(advert.price2)}
                                </div>
                              )}
                            <div className={styles.textPriceDesc}>
                              {dealer.pricingSchema2}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        ))}
    </div>
  );
};
